import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { sendTrackEvent } from '@edx/frontend-platform/analytics';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import classNames from 'classnames';

import { getConfig } from "@edx/frontend-platform";
import { Link } from "react-router-dom";
import { Button } from '@openedx/paragon';
import { AlertList } from '../../generic/user-messages';

import CourseDates from './widgets/CourseDates';
import CourseHandouts from './widgets/CourseHandouts';
import StartOrResumeCourseCard from './widgets/StartOrResumeCourseCard';
import WeeklyLearningGoalCard from './widgets/WeeklyLearningGoalCard';
import CourseTools from './widgets/CourseTools';
import { fetchOutlineTab } from '../data';
import messages from './messages';
import Section from './Section';
import ShiftDatesAlert from '../suggested-schedule-messaging/ShiftDatesAlert';
import UpgradeNotification from '../../generic/upgrade-notification/UpgradeNotification';
import UpgradeToShiftDatesAlert from '../suggested-schedule-messaging/UpgradeToShiftDatesAlert';
import useCertificateAvailableAlert from './alerts/certificate-status-alert';
import useCourseEndAlert from './alerts/course-end-alert';
import useCourseStartAlert from '../../alerts/course-start-alert';
import usePrivateCourseAlert from './alerts/private-course-alert';
import useScheduledContentAlert from './alerts/scheduled-content-alert';
import { useModel } from '../../generic/model-store';
import WelcomeMessage from './widgets/WelcomeMessage';
import ProctoringInfoPanel from './widgets/ProctoringInfoPanel';
import AccountActivationAlert from '../../alerts/logistration-alert/AccountActivationAlert';

/** [MM-P2P] Experiment */
import { initHomeMMP2P, MMP2PFlyover } from '../../experiments/mm-p2p';
import { ReactComponent as Plus } from '../../svg/plus.svg';
import { ReactComponent as Minus } from '../../svg/minus.svg';

import { getDashboardPlatformURL } from '../../utils/utility';
import CustomFooter from '../../footer/CustomFooter';

function OutlineTab({ intl }) {
  const {
    courseId,
    proctoringPanelStatus,
  } = useSelector(state => state.courseHome);

  const {
    isSelfPaced,
    org,
    title,
    userTimezone,
  } = useModel('courseHomeMeta', courseId);

  const {
    accessExpiration,
    courseBlocks: {
      courses,
      sections,
      sequences,
    },
    courseGoals: {
      selectedGoal,
      weeklyLearningGoalEnabled,
    } = {},
    datesBannerInfo,
    datesWidget: {
      courseDateBlocks,
    },
    enableProctoredExams,
    offer,
    timeOffsetMillis,
    verifiedMode,
  } = useModel('outline', courseId);

  const {
    marketingUrl,
  } = useModel('coursewareMeta', courseId);

  const [expandAll, setExpandAll] = useState(false);

  const eventProperties = {
    org_key: org,
    courserun_key: courseId,
  };

  // Below the course title alerts (appearing in the order listed here)
  const courseStartAlert = useCourseStartAlert(courseId);
  const courseEndAlert = useCourseEndAlert(courseId);
  const certificateAvailableAlert = useCertificateAvailableAlert(courseId);
  const privateCourseAlert = usePrivateCourseAlert(courseId);
  const scheduledContentAlert = useScheduledContentAlert(courseId);

  const rootCourseId = courses && Object.keys(courses)[0];

  const hasDeadlines = courseDateBlocks && courseDateBlocks.some(x => x.dateType === 'assignment-due-date');

  const logUpgradeToShiftDatesLinkClick = () => {
    sendTrackEvent('edx.bi.ecommerce.upsell_links_clicked', {
      ...eventProperties,
      linkCategory: 'personalized_learner_schedules',
      linkName: 'course_home_upgrade_shift_dates',
      linkType: 'button',
      pageName: 'course_home',
    });
  };

  // const isEnterpriseUser = () => {
  //   const authenticatedUser = getAuthenticatedUser();
  //   const userRoleNames = authenticatedUser ? authenticatedUser.roles.map(role => role.split(':')[0]) : [];

  //   return userRoleNames.includes('enterprise_learner');
  // };

  /** [[MM-P2P] Experiment */
  // const MMP2P = initHomeMMP2P(courseId);

  /** show post enrolment survey to only B2C learners */
  // const learnerType = isEnterpriseUser() ? 'enterprise_learner' : 'b2c_learner';
  
  const [lastVisitedSection, lastVisitedSequence] = useMemo(() => {
    let lastVisitedSection = null;
    let lastVisitedSequence = null;

    for (const sectionId in sections) {
      if (sections[sectionId].lastVisited) {
        lastVisitedSection = sections[sectionId];
        break;
      }
    }

    if (lastVisitedSection) {
      for (const sequenceId of lastVisitedSection.sequenceIds) {
        if (sequences[sequenceId].lastVisited) {
          lastVisitedSequence = sequences[sequenceId];
          break;
        }
      }
    }

    return [lastVisitedSection, lastVisitedSequence];
  }, [sections, sequences]);
  
  return (
    <>
      {/** [MM-P2P] Experiment (className for optimizely trigger) */}
      <div className="container">
        <div className="row course-outline-tab main-content-wrapper">
          {/* <AccountActivationAlert />
          <div className="col-12">
            <AlertList
              topic="outline-private-alerts"
              customAlerts={{
                ...privateCourseAlert,
              }}
            />
          </div> */}

          <div className="col col-12">
            <div className="row">
              <div className="col col-12">

                <div className="position-relative breadcrumbs-wrapper pb-2 d-none d-lg-block">
                    <nav aria-label="breadcrumb" className="mt-4 d-inline-block">
                        <ol className="list-unstyled d-flex flex-wrap flex-lg-nowrap align-items-center m-0">
                            <li>
                              <Link
                                className="flex-shrink-0"
                                to={`#`}
                                onClick={() => window.location.assign(getDashboardPlatformURL())}
                                >
                                  {intl.formatMessage(messages.homeBreadCrumb)}
                              </Link>
                            </li>
                            <li><span>/</span></li>
                            <li><a className="flex-shrink-0 color-dark-grey" href="#">{title}</a></li>
                        </ol>
                    </nav>
                </div>

                <div className="overview-header">
                  {/* <span className="course-title">{title}</span> */}
                  <StartOrResumeCourseCard
                    courseName={title}
                    lastVisitedSection={lastVisitedSection}
                    lastVisitedSequence={lastVisitedSequence}
                  />
                </div>
              </div>

              <div className="col col-12 col-md-8">
                {/** [MM-P2P] Experiment (the conditional) */}
                {/* { !MMP2P.state.isEnabled
                  && (
                  <AlertList
                    topic="outline-course-alerts"
                    className="mb-3"
                    customAlerts={{
                      ...certificateAvailableAlert,
                      ...courseEndAlert,
                      ...courseStartAlert,
                      ...scheduledContentAlert,
                    }}
                  />
                  )}
                {isSelfPaced && hasDeadlines && !MMP2P.state.isEnabled && (
                  <>
                    <ShiftDatesAlert model="outline" fetch={fetchOutlineTab} />
                    <UpgradeToShiftDatesAlert model="outline" logUpgradeLinkClick={logUpgradeToShiftDatesLinkClick} />
                  </>
                )}
                <WelcomeMessage courseId={courseId} /> */}
                {rootCourseId && (
                  <>
                    <div className="row w-100 m-0 justify-content-end expand-collaps-btn">
                      <div className="col-12 col-md-auto p-0">
                        <Button
                          className={classNames({ 'color-primary': expandAll })}
                          variant="link"
                          block
                          onClick={() => {
                            setExpandAll(!expandAll);
                          }}
                        >
                          {expandAll ? (
                            <Minus className="mr-2" />
                          ) : (
                            <Plus className="mr-2" />
                          )}
                          {expandAll
                            ? intl.formatMessage(messages.collapseAll)
                            : intl.formatMessage(messages.expandAll)}
                        </Button>
                      </div>
                    </div>
                    <ol id="courseHome-outline" className="list-unstyled">
                      {courses[rootCourseId].sectionIds.map(
                        (sectionId, index) => (
                          <Section
                            sectionNumber={index + 1}
                            key={sectionId}
                            courseId={courseId}
                            defaultOpen={sections[sectionId].expand}
                            expand={expandAll}
                            section={sections[sectionId]}
                          />
                        )
                      )}
                    </ol>
                  </>
                )}
              </div>
              {rootCourseId && (
                <div className="col col-12 col-md-4">
                  <div className="course-stats-sidebar">
                    {/* <ProctoringInfoPanel />
                    { /** Defer showing the goal widget until the ProctoringInfoPanel has resolved or has been determined as
                    disabled to avoid components bouncing around too much as screen is rendered *-/ }
                    {(!enableProctoredExams || proctoringPanelStatus === 'loaded') && weeklyLearningGoalEnabled && (
                      <WeeklyLearningGoalCard
                        daysPerWeek={selectedGoal && 'daysPerWeek' in selectedGoal ? selectedGoal.daysPerWeek : null}
                        subscribedToReminders={selectedGoal && 'subscribedToReminders' in selectedGoal ? selectedGoal.subscribedToReminders : false}
                      />
                    )} */}
                    <CourseTools />
                    {/** [MM-P2P] Experiment (conditional) */}
                    {/* { MMP2P.state.isEnabled
                      ? <MMP2PFlyover isStatic options={MMP2P} />
                      : (
                        <UpgradeNotification
                          offer={offer}
                          verifiedMode={verifiedMode}
                          accessExpiration={accessExpiration}
                          contentTypeGatingEnabled={datesBannerInfo.contentTypeGatingEnabled}
                          marketingUrl={marketingUrl}
                          upsellPageName="course_home"
                          userTimezone={userTimezone}
                          shouldDisplayBorder
                          timeOffsetMillis={timeOffsetMillis}
                          courseId={courseId}
                          org={org}
                        />
                      )} */}
                    {/* <CourseDates
                      /** [MM-P2P] Experiment *-/
                      mmp2p={MMP2P}
                    /> */}
                    {/* <CourseHandouts /> */}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

OutlineTab.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(OutlineTab);
